import { useState, useCallback, useRef } from 'react';
import type { FacetResult } from '@orama/orama';
import { createSearchTokenizer } from '@/services/search-service/orama-schema';

import { useSearchIndexUpdateListener } from '@/hooks/use-search-index-update-listener';

import { capture } from '@/analytics';
import type { SearchService } from '@/services/search-service';
import type { SearchResult, OramaSearchParams } from '@/services/search-service/types';
import type { SortString } from '@/constants/types';

export type SearchQuery = {
  term?: string;
  where?: {
    ids?: string | string[];
    types?: string | string[];
    tagIds?: string | string[];
    excludeTagIds?: string[];
    excludeSourceKinds?: string[];
    availabilityScopes?: string | string[];
    alias?: string | string[];
    updatedTo?: number;
    updatedSince?: number;
  };
  tolerance?: number;
  threshold?: number;
  sort?: SortString;
  highlightMatches?: boolean;
  includeStats?: boolean;
  isUserSearch?: boolean;
};

type SortOrder = 'ASC' | 'DESC' | undefined;

type ResultsAndLoadingState = {
  data: SearchResult[];
  stats?: SearchStats;
  isInitialLoading: boolean;
};

export type SearchStats = {
  notes: number;
  templates: number;
  blocks: number;
  tags: number;
  instructions: number;
  userSources: number;
  total: number;
};

const sortArrays = (_: string, v: unknown) => (Array.isArray(v) ? [...v].sort() : v);

const DEFAULT_LIMIT = 300; // Override via buildSearchParams paging for archive browse lazy load
const RELEVANCE_CUTOFF_RATIO = 0.05;
export const searchablePropertyBoost = { aliases: 10, name: 8, content: 1, tagNames: 2 };
export const searchableProperties = Object.keys(searchablePropertyBoost);

// Same defaults as the worker's index, so query tokens match indexed tokens.
const tokenizer = createSearchTokenizer();
const tokenize = (text: string): string[] => tokenizer.tokenize(text);

// Shared engine behind useSearch and useArchivedSearch: fetch + relevance pipeline +
// index-update subscription against the given SearchService instance. Loading gates
// differ per wrapper (initialSyncComplete vs the archived instance's own IndexStatus),
// so this returns the raw isInitialLoading instead of a composed isLoading.
export const useSearchCore = (service: SearchService, query: SearchQuery, enabled: boolean) => {
  const [searchResults, setSearchResults] = useState<ResultsAndLoadingState>({
    data: [],
    isInitialLoading: true,
  });
  const lastCapturedQueryRef = useRef<string | undefined>(undefined);
  const activeQueryRef = useRef<string | undefined>(undefined);

  const serializedQuery = JSON.stringify(query, sortArrays);
  activeQueryRef.current = serializedQuery;

  const fetchResults = useCallback(async () => {
    try {
      const term = (query.term ?? '').trim();
      const searchOptions = {
        highlightMatches: query.highlightMatches,
        includeFacets: query.includeStats && !term,
      };
      if (!term) {
        // Clearing the term resets the metric dedupe so re-searching the same term emits again.
        lastCapturedQueryRef.current = undefined;
        const response = await service.search(buildSearchParams(query), searchOptions);
        setSearchResults({
          data: response.hits,
          stats: transformFacetsToStats(response.facets, response.count),
          isInitialLoading: false,
        });
        return;
      }
      const startedAtMs = performance.now();
      const response = await runTermSearch(service, query, searchOptions);
      let hits = applyRelevanceCutoff(response.hits);
      if (query.sort) {
        hits = sortHits(hits, query.sort);
      } else {
        hits = promoteNameMatches(hits, term);
      }
      setSearchResults({
        data: hits,
        stats: query.includeStats ? computeStatsFromHits(hits) : undefined,
        isInitialLoading: false,
      });
      // Suppress the capture if the query changed while this search was in flight (e.g. cleared).
      if (query.isUserSearch && activeQueryRef.current === serializedQuery && lastCapturedQueryRef.current !== serializedQuery) {
        lastCapturedQueryRef.current = serializedQuery;
        captureSearchPerformed(startedAtMs, hits.length);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults({ data: [], isInitialLoading: false });
    }
  }, [service, serializedQuery]);

  useSearchIndexUpdateListener({
    service,
    refetch: fetchResults,
    enabled,
  });

  return {
    data: searchResults.data,
    stats: searchResults.stats,
    refetch: fetchResults,
    isInitialLoading: searchResults.isInitialLoading,
  };
};

// Gated to real user searches only: the caller must opt in via query.isUserSearch
// (set on the library search box, not on autocomplete/filter surfaces), the term must
// be non-empty, and the serialized query must differ from the last captured one so
// index-update refetches and repeated refetch() of an unchanged query never re-emit,
// and the query must still be the active one at resolve time so a search that finishes
// after the user cleared or changed the term never emits.
// Numeric only: the query term and any document content are PHI and must never enter
// the payload.
const captureSearchPerformed = (startedAtMs: number, resultCount: number): void => {
  try {
    capture('search_performed', {
      latency_ms: Math.round(performance.now() - startedAtMs),
      result_count: resultCount,
    });
  } catch {
    // Telemetry must never throw into fetchResults' catch, which discards results.
  }
};

type TermSearchOptions = Parameters<SearchService['search']>[1];

// AND-first: threshold 0 requires every query word to co-occur in one field of a
// doc; on 0 hits fall back to the looser threshold so queries never dead-end.
// Explicit-threshold callers keep full control and skip the fallback.
const runTermSearch = async (service: SearchService, query: SearchQuery, options: TermSearchOptions) => {
  // Never send sortBy on term searches: Orama truncates to `limit` in sorted
  // order, and applyRelevanceCutoff needs the score-ranked window. Callers
  // sort client-side after the cutoff (sortHits).
  const engineQuery = { ...query, sort: undefined };
  if (engineQuery.threshold !== undefined) {
    return service.search(buildSearchParams(engineQuery), options);
  }
  const strict = await service.search(buildSearchParams({ ...engineQuery, threshold: 0 }), options);
  if (strict.hits.length > 0) {
    return strict;
  }
  return service.search(buildSearchParams({ ...engineQuery, threshold: 0.1 }), options);
};

// Scoped per document.type so a hit survives iff it would survive a single-type
// query, keeping type-filter counts consistent (boosts span 10x across fields).
const applyRelevanceCutoff = (hits: SearchResult[]): SearchResult[] => {
  if (hits.length === 0) return hits;
  const topScoreByType = new Map<string, number>();
  for (const hit of hits) {
    const current = topScoreByType.get(hit.document.type) ?? 0;
    if (hit.score > current) topScoreByType.set(hit.document.type, hit.score);
  }
  return hits.filter((hit) => hit.score >= RELEVANCE_CUTOFF_RATIO * (topScoreByType.get(hit.document.type) ?? 0));
};

// Sort survives client-side so it never influences which hits pass the cutoff:
// docs missing the property sort last in both directions; Array.sort stability
// preserves the score-ranked order among ties.
const sortHits = (hits: SearchResult[], sort: SortString): SearchResult[] => {
  const { property, order } = parseSortString(sort);
  const direction = order === 'DESC' ? -1 : 1;
  return [...hits].sort((a, b) => {
    const aValue = a.document[property as keyof typeof a.document];
    const bValue = b.document[property as keyof typeof b.document];
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction * aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
    }
    return direction * (Number(aValue) - Number(bValue));
  });
};

// Orama prefix-matches every query token, so one strong prefix hit on a short
// name (or an alias) ("chest pa" -> "chesterfield paracetamol", "1/5/26" -> "123
// 567 2699") can outscore a title that contains every token. Docs whose name
// holds all query tokens rank first; only the last token, still being typed, may
// match as a prefix.
const promoteNameMatches = (hits: SearchResult[], term: string): SearchResult[] => {
  const queryTokens = tokenize(term);
  if (queryTokens.length === 0) return hits;
  const nameMatches: SearchResult[] = [];
  const rest: SearchResult[] = [];
  for (const hit of hits) {
    (namedForQuery(hit.document, queryTokens) ? nameMatches : rest).push(hit);
  }
  return [...nameMatches, ...rest];
};

// A hit is promoted when its name OR any single alias contains all query tokens.
// Each value is checked in full on its own: tokens are never satisfied across
// name+alias or across two aliases combined.
const namedForQuery = (document: SearchResult['document'], queryTokens: string[]): boolean =>
  [document.name ?? '', ...(document.aliases ?? [])].some((value) => containsAllTokens(value, queryTokens));

const containsAllTokens = (value: string, queryTokens: string[]): boolean => {
  const valueTokens = tokenize(value);
  if (valueTokens.length === 0) return false;
  const exactTokens = new Set(valueTokens);
  const lastIndex = queryTokens.length - 1;
  return queryTokens.every((token, index) =>
    index < lastIndex
      ? exactTokens.has(token)
      : exactTokens.has(token) || valueTokens.some((valueToken) => valueToken.startsWith(token))
  );
};

const ZERO_STATS = {
  notes: 0,
  templates: 0,
  blocks: 0,
  tags: 0,
  instructions: 0,
  userSources: 0,
} satisfies Omit<SearchStats, 'total'>;

const statTypes = Object.keys(ZERO_STATS) as Array<keyof typeof ZERO_STATS>;

const emptyStats = (total: number): SearchStats => ({ ...ZERO_STATS, total });

const computeStatsFromHits = (hits: SearchResult[]): SearchStats => {
  const stats = emptyStats(hits.length);
  for (const hit of hits) {
    const type = hit.document.type as keyof typeof ZERO_STATS;
    if (type in ZERO_STATS) stats[type] += 1;
  }
  return stats;
};

export const buildSearchParams = (query: SearchQuery, paging?: { limit: number; offset?: number }): OramaSearchParams => {
  const { where = {}, sort, tolerance, threshold = 0.1 } = query;
  const term = (query.term ?? '').trim();
  const { ids, types, tagIds, excludeTagIds, excludeSourceKinds, availabilityScopes, alias, updatedSince, updatedTo } = where;

  const fieldFilters: Record<string, unknown> = {};

  if (ids) fieldFilters.id = ids;
  if (types) fieldFilters.type = types;
  if (availabilityScopes?.length) {
    fieldFilters.availabilityScope = { in: Array.isArray(availabilityScopes) ? availabilityScopes : [availabilityScopes] };
  }
  if (tagIds?.length) {
    const tagIdArray = Array.isArray(tagIds) ? tagIds : [tagIds];
    fieldFilters.tagIds = { containsAll: tagIdArray };
  }

  if (updatedSince || updatedTo) {
    let updatedAt;

    if (updatedSince && updatedTo) {
      updatedAt = { between: [updatedSince, updatedTo] };
    } else if (updatedSince) {
      updatedAt = { gte: updatedSince };
    } else {
      updatedAt = { lte: updatedTo };
    }

    fieldFilters.updatedAt = updatedAt;
  }

  if (alias) fieldFilters.aliases = alias;

  const andClauses: Record<string, unknown>[] = [];

  if (Object.keys(fieldFilters).length > 0) {
    andClauses.push(fieldFilters);
  }

  if (excludeTagIds?.length) {
    andClauses.push({ not: { tagIds: { containsAny: excludeTagIds } } });
  }

  if (excludeSourceKinds?.length) {
    andClauses.push({ not: { sourceKind: { in: excludeSourceKinds } } });
  }

  const whereClause = andClauses.length > 1 ? { and: andClauses } : andClauses[0] || {};

  let sortBy: { property: string; order: SortOrder } | undefined;
  if (sort) {
    sortBy = parseSortString(sort);
  } else if (!term) {
    sortBy = { property: 'updatedAt', order: 'DESC' };
  }

  // Empty term must take Orama's pure where-filter path: passing `properties`
  // forces full-text, which drops docs with no searchable tokens (untitled notes).
  return {
    term,
    limit: paging?.limit ?? DEFAULT_LIMIT,
    ...(paging?.offset !== undefined && { offset: paging.offset }),
    where: whereClause,
    ...(term && {
      properties: searchableProperties,
      boost: searchablePropertyBoost,
      threshold,
      ...(tolerance !== undefined && { tolerance }),
    }),
    ...(sortBy && { sortBy }),
  } as OramaSearchParams;
};

const transformFacetsToStats = (facets?: FacetResult, count?: number): SearchStats | undefined => {
  if (!facets?.type) {
    return undefined;
  }
  const typeValues = facets.type.values ?? {};
  const stats = emptyStats(count ?? 0);
  for (const type of statTypes) {
    stats[type] = typeValues[type] ?? 0;
  }
  return stats;
};

const parseSortString = (sort: SortString): { property: string; order: SortOrder } => {
  const colonIdx = sort.indexOf(':');
  return { property: sort.slice(0, colonIdx), order: sort.slice(colonIdx + 1).toUpperCase() as SortOrder };
};
